import { useState } from 'react'
import { ExternalLink, Globe } from 'lucide-react'
import { WebCitation } from '@digital-twins/shared-types'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import i18n, { intlLocaleForUi } from '@/i18n'

/**
 * One chip per URL: several web citations pointing at the same page collapse
 * into a single group so the same source is never listed twice. Mirrors
 * `CitationDocumentGroup` / `groupCitationsByDocument`, keyed on `url`
 * instead of a document token — a web citation has no document row to key on
 * (see `partitionAssistantCitations.ts`).
 *
 * The chip stays small (favicon + publisher or host). Title, date and
 * destination live on hover. Favicons come from the hostname via Google's
 * public icon endpoint — no page fetch, no new citation field. A failed
 * load falls back to the globe so a broken image never sits in the pill.
 */
export interface WebCitationGroup {
  key: string
  citations: Array<WebCitation>
}

export function groupWebCitationsByUrl(
  citations: Array<WebCitation>,
): Array<WebCitationGroup> {
  const groups: Array<WebCitationGroup> = []
  const indexByKey = new Map<string, number>()

  citations.forEach((citation) => {
    const key = citation.url || citation.title || 'web-source'
    const existing = indexByKey.get(key)
    if (existing == null) {
      indexByKey.set(key, groups.length)
      groups.push({ key, citations: [citation] })
      return
    }
    groups[existing].citations.push(citation)
  })

  return groups
}

/** `nytimes.com`, never `www.nytimes.com` — the `www.` prefix names nothing. */
function hostnameFromUrl(url: string): string | null {
  if (!url) {
    return null
  }
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return null
  }
}

function formatPublishedDate(value: string | undefined): string | null {
  if (!value) {
    return null
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return date.toLocaleDateString(intlLocaleForUi(i18n.language), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function chipLabel(citation: WebCitation, hostname: string | null): string {
  return citation.publisher?.trim() || hostname || citation.title?.trim() || citation.url || i18n.t('threads.scope.chipWeb', { ns: 'app' })
}

/**
 * Hostname only — never the article path — so the request cannot be used as
 * an open redirect into a publisher URL. Returns null for anything that is
 * not a plain host.
 */
export function faviconUrlForHostname(hostname: string): string | null {
  if (!/^[a-z0-9.-]+$/i.test(hostname)) {
    return null
  }

  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostname)}&sz=32`
}

interface SourceFaviconProps {
  hostname: string | null
}

function SourceFavicon(props: SourceFaviconProps) {
  const { hostname } = props

  const [failed, setFailed] = useState(false)

  const src = hostname ? faviconUrlForHostname(hostname) : null

  if (!src || failed) {
    return (
      <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
    )
  }

  return (
    <img
      src={src}
      alt=""
      width={14}
      height={14}
      loading="lazy"
      referrerPolicy="no-referrer"
      className="h-3.5 w-3.5 shrink-0 rounded-full"
      data-testid="thread-web-source-card-favicon"
      onError={() => setFailed(true)}
    />
  )
}

interface ThreadWebSourceCardProps {
  group: WebCitationGroup
}

export function ThreadWebSourceCard(props: ThreadWebSourceCardProps) {
  const { group } = props

  const first = group.citations[0]

  if (!first) {
    return null
  }

  const hostname = hostnameFromUrl(first.url)
  const publisherLabel = first.publisher?.trim() || hostname
  const dateLabel = formatPublishedDate(first.publishedAt)
  const metaParts = [publisherLabel, dateLabel].filter(
    (value): value is string => Boolean(value),
  )
  const title = first.title?.trim() || first.url
  const label = chipLabel(first, hostname)

  const preview = (
    <div className="space-y-2" data-testid="thread-web-source-card-preview">
      <Heading level={3} size="sm" weight="medium" className="leading-snug">
        {title}
      </Heading>
      <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
        <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
        <Text as="span" size="xs" color="muted">
          {metaParts.length > 0 ? metaParts.join(' · ') : 'Web'}
        </Text>
      </div>
    </div>
  )

  const chipClassName =
    'inline-flex max-w-[12rem] items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-left'

  const chipInner = (
    <>
      <SourceFavicon hostname={hostname} />
      <Text as="span" size="xs" className="truncate">
        {label}
      </Text>
    </>
  )

  const trigger = first.url ? (
    // eslint-disable-next-line react/jsx-no-target-blank -- `noopener` alone already closes the tabnabbing vector this rule guards against; `noreferrer` is deliberately omitted so the source publisher still sees it was reached from here.
    <a
      href={first.url}
      target="_blank"
      rel="noopener"
      className={chipClassName}
      data-testid="thread-web-source-card-link"
    >
      {chipInner}
    </a>
  ) : (
    <span className={chipClassName}>{chipInner}</span>
  )

  return (
    <article
      className="inline-flex"
      data-testid="thread-web-source-card"
      data-url-key={group.key}
    >
      <HoverCard openDelay={150} closeDelay={120}>
        <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
        <HoverCardContent
          align="start"
          side="top"
          sideOffset={8}
          className="w-80"
        >
          {preview}
        </HoverCardContent>
      </HoverCard>
    </article>
  )
}
